/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import {
    FaCalendarAlt,
    FaGraduationCap,
    FaShoppingBag,
    FaBox,
    FaCheckCircle,
    FaReceipt
} from 'react-icons/fa';
import { getDomainLabelsByType, DOMAIN_TYPES, getDomainColorsByType, } from '../../../../../../domain/Domain';

const useDomain = () => {
    const domainType = localStorage.getItem("domainType") || DOMAIN_TYPES.RETAIL;

    const isFlexOfficeDomain = domainType === DOMAIN_TYPES.FLEX_OFFICE;
    const isEducationDomain = domainType === DOMAIN_TYPES.EDUCATION;
    const isRetailDomain = domainType === DOMAIN_TYPES.RETAIL;
    const isInstitutionDomain = domainType === DOMAIN_TYPES.INSTITUTION;
    const isAssessmentDomain = domainType === DOMAIN_TYPES.ASSESSMENT;
    const isServicesDomain = domainType === DOMAIN_TYPES.SERVICES;
    const isITDomain = domainType === DOMAIN_TYPES.IT;

    const colors = getDomainColorsByType(domainType);
    const labels = getDomainLabelsByType(domainType);
    const getDomainPrimaryColor = () => colors.primary || 'var(--flex-primary, #2563eb)';
    const getDomainSecondaryColor = () => colors.secondary || 'var(--flex-secondary, #0f172a)';
    const getDomainBackgroundColor = () => {
        if (isFlexOfficeDomain) return 'var(--flex-primary-soft, rgba(37, 99, 235, 0.16))';
        if (isEducationDomain) return 'var(--education-primary-soft, rgba(116, 110, 172, 0.1))';
        return `${getDomainPrimaryColor()}15`;
    };
    const getDomainBorderColor = () => {
        if (isFlexOfficeDomain) return 'var(--flex-primary-muted, rgba(37, 99, 235, 0.3))';
        if (isEducationDomain) return 'var(--education-primary-muted, rgba(116, 110, 172, 0.2))';
        return `${getDomainPrimaryColor()}30`;
    };
    const getDomainTextColor = () => getDomainPrimaryColor();

    const getDocumentTypeDisplay = () => isEducationDomain ? 'Receipt' : labels.booking;
    const getDocumentTypePluralDisplay = () => isEducationDomain ? 'Bookings' : `${labels.booking}s`;

    const getDomainIcon = () => {
        if (isFlexOfficeDomain) return <FaCalendarAlt className="w-10 h-10" />;
        if (isEducationDomain) return <FaGraduationCap className="w-10 h-10" />;
        if (isRetailDomain) return <FaShoppingBag className="w-10 h-10" />;
        if (isInstitutionDomain) return <FaBox className="w-10 h-10" />;
        if (isAssessmentDomain) return <FaCheckCircle className="w-10 h-10" />;
        if (isServicesDomain || isITDomain) return <FaReceipt className="w-10 h-10" />;
        return <FaReceipt className="w-10 h-10" />;
    };

    const getLoadingText = () => {
        if (isRetailDomain) return 'Loading Orders...';
        if (isEducationDomain) return 'Loading your enrollments...';
        return 'Loading your Bookings...';
    };

    const getEmptyStateText = () => {
        if (isFlexOfficeDomain) return "You don't have any bookings yet.";
        if (isEducationDomain) return "You don't have any purchase yet.";
        if (isRetailDomain) return "You don't have any orders yet.";
        if (isInstitutionDomain) return "You don't have any bookings yet.";
        if (isAssessmentDomain) return "You don't have any assessments yet.";
        if (isServicesDomain || isITDomain) return "You don't have any service bookings yet.";
        return "You don't have any bookings yet.";
    };

    const getExploreButtonText = () => {
        if (isFlexOfficeDomain) return `Explore ${labels.space}s`;
        if (isEducationDomain) return 'Explore Courses';
        if (isRetailDomain) return 'Browse Products';
        if (isInstitutionDomain) return 'Browse Programs';
        if (isAssessmentDomain) return 'Explore Assessments';
        if (isServicesDomain || isITDomain) return 'Explore Services';
        return 'Explore';
    };

    const getExploreNavigationPath = () => {
        if (isFlexOfficeDomain) return '/flex-home';
        if (isEducationDomain) return '/education-home';
        if (isRetailDomain) return '/retail-home';
        if (isInstitutionDomain) return '/institution-home';
        if (isAssessmentDomain) return '/assessment-home';
        if (isServicesDomain) return '/services-home';
        if (isITDomain) return '/it-home';
        return '/home';
    };

    const getActionVerb = () => {
        if (isEducationDomain) return 'Enrolled';
        if (isFlexOfficeDomain) return 'Booked';
        if (isRetailDomain) return 'Ordered';
        return 'Booked';
    };

    return {
        domainType,
        isFlexOfficeDomain,
        isEducationDomain,
        isRetailDomain,
        isInstitutionDomain,
        isAssessmentDomain,
        isServicesDomain,
        isITDomain,
        labels,
        colors,
        getDomainPrimaryColor,
        getDomainSecondaryColor,
        getDomainBackgroundColor,
        getDomainBorderColor,
        getDomainTextColor,
        getDocumentTypeDisplay,
        getDocumentTypePluralDisplay,
        getDomainIcon,
        getLoadingText,
        getEmptyStateText,
        getExploreButtonText,
        getExploreNavigationPath,
        getActionVerb
    };
};

export default useDomain;
